import {
  chakra,
  FormControlOptions,
  forwardRef,
  HTMLChakraProps,
  omitThemingProps,
  ThemingProps,
  useFormControl,
  useMergeRefs,
  useMultiStyleConfig,
  usePopper,
} from '@chakra-ui/react';
import { mergeWith } from '@chakra-ui/utils';
import { useSelect } from 'downshift'; // using version 6.1.3
import { SelectIcon } from 'features';
import * as React from 'react';

export interface SelectProps
  extends FormControlOptions,
    ThemingProps<'Select'>,
    Omit<
      HTMLChakraProps<'button'>,
      'disabled' | 'required' | 'readOnly' | 'size' | 'value' | 'onChange'
    > {
  placeholder?: string;
  value?: string | undefined | undefined;
  // onChange?: (item: string | undefined | undefined) => void;
  onChange?: any;
}

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (props, ownRef) => {
    const { id, value, children, placeholder, onChange, ...rest } =
      omitThemingProps(props);
    const ownButtonProps = useFormControl<HTMLButtonElement>(rest);
    const styles = useMultiStyleConfig('CustomSelect', props);

    const validChildren = React.useMemo(
      () =>
        React.Children.toArray(children)
          .filter<
            React.ReactElement<{ value: string; children?: React.ReactElement }>
          >(React.isValidElement)
          .filter((child) => 'value' in child.props),
      [children]
    );

    const items = validChildren.map((child) => child.props.value);

    const {
      isOpen,
      selectedItem,
      getToggleButtonProps,
      getMenuProps,
      getItemProps,
    } = useSelect({
      id,
      items,
      selectedItem: value,
      onSelectedItemChange: (val) => onChange?.(val.selectedItem),
    });

    const { referenceRef: popperRef, getPopperProps } = usePopper({
      enabled: isOpen,
      gutter: 2,
    });
    const { ref: useSelectToggleButtonRef, ...useSelectToggleButtonProps } =
      getToggleButtonProps();

    const toggleButtonRef = useMergeRefs(
      ownRef,
      useSelectToggleButtonRef,
      popperRef
    );
    const toggleButtonProps = mergeWith(
      ownButtonProps,
      useSelectToggleButtonProps
    );

    return (
      <chakra.div position="relative">
        <chakra.button
          ref={toggleButtonRef}
          __css={styles.field}
          data-focus-visible-added={isOpen}
          {...toggleButtonProps}
        >
          {validChildren.find((child) => child.props.value === selectedItem)
            ?.props.children ||
            selectedItem || <chakra.span>{placeholder}</chakra.span>}
          <SelectIcon />
        </chakra.button>
        <chakra.div
          zIndex="1"
          width="100%"
          {...mergeWith(getPopperProps(), {
            style: { visibility: isOpen ? 'visible' : 'hidden' },
          })}
        >
          <chakra.ul
            __css={styles.menu}
            data-focus-visible-added={isOpen}
            {...getMenuProps()}
          >
            {isOpen &&
              validChildren.map((item, index) =>
                React.cloneElement(item, {
                  ...getItemProps({ item: item.props.value, index }),
                  // @ts-ignore
                  __css: styles.option,
                })
              )}
          </chakra.ul>
        </chakra.div>
      </chakra.div>
    );
  }
);

Select.displayName = 'Select';

export interface OptionProps extends HTMLChakraProps<'li'>, ThemingProps {
  value: string;
}

export const Option = forwardRef<OptionProps, 'li'>((props, ref) => {
  const { children, value, ...rest } = omitThemingProps(props);
  const { option } = useMultiStyleConfig('CustomSelect', props);
  return (
    <chakra.li ref={ref} __css={option} {...rest}>
      {children || value}
    </chakra.li>
  );
});
